import { frontendErrorsFormat } from "@/features/observability/components/frontend-errors-format";
import type { FrontendErrorsRange } from "@/features/observability/hooks/frontend-errors-range";
import type { FrontendErrorsCategory } from "@/features/observability/types/frontend-errors.types";
import {
    platformErrorsContract,
    type PlatformSeverity,
    type PlatformEnvironment,
    type PlatformErrorsQueryParams,
} from "@/features/observability/platform-errors/types/platform-errors.types";

export type PlatformErrorsPreset = "1h" | "6h" | "24h" | "7d";

export type PlatformErrorsRangeChoice = PlatformErrorsPreset | "custom";

export type PlatformErrorsFilters = {
    environment: PlatformEnvironment | null;
    range: PlatformErrorsRangeChoice;
    customFrom: number | null;
    customTo: number | null;
    service: string | null;
    severity: PlatformSeverity | null;
    resourceType: string | null;
    errorCode: string | null;
    category: FrontendErrorsCategory | null;
};

export type PlatformErrorsResolvedRange = { range: FrontendErrorsRange | null; clamped: boolean };

export type PlatformErrorsDimension = "service" | "resourceType" | "errorCode";

const HOUR_MS = 60 * 60 * 1000;
const DAY_MS = 24 * HOUR_MS;
const MAX_WINDOW_MS = 7 * DAY_MS;
// The platform alias keeps 13 days (backend answers `window_outside_retention` past it). The reader checks
// with its own clock, and a view stays open: an hour inside the limit, re-applied at every read (see the page).
const RETENTION_MS = 13 * DAY_MS;
const RETENTION_MARGIN_MS = 60 * 60 * 1000;
const DEFAULT_RANGE: PlatformErrorsPreset = "24h";

const PRESET_MS: Record<PlatformErrorsPreset, number> = { "1h": HOUR_MS, "6h": 6 * HOUR_MS, "24h": DAY_MS, "7d": MAX_WINDOW_MS };
const PRESETS: PlatformErrorsPreset[] = ["1h", "6h", "24h", "7d"];

// The backend's keyword widths (validator: service 100, resourceType 64, errorCode 128).
const DIMENSION_MAX: Record<PlatformErrorsDimension, number> = { service: 100, resourceType: 64, errorCode: 128 };

const isPreset = (value: string | null): value is PlatformErrorsPreset =>
    value !== null && PRESETS.some((preset) => preset === value);

const toEpoch = (value: string | null): number | null => {
    if (value === null || !/^\d{1,15}$/.test(value)) return null;
    return Number(value);
};

const toDimension = (dimension: PlatformErrorsDimension, value: string | null): string | null => {
    if (value === null) return null;
    const trimmed = value.trim();
    if (trimmed.length === 0 || trimmed.length > DIMENSION_MAX[dimension]) return null;
    return trimmed;
};

const rangeOf = (search: URLSearchParams): Pick<PlatformErrorsFilters, "range" | "customFrom" | "customTo"> => {
    const range = search.get("range");
    if (isPreset(range)) return { range, customFrom: null, customTo: null };
    if (range === "custom") return { range: "custom", customFrom: toEpoch(search.get("from")), customTo: toEpoch(search.get("to")) };
    return { range: DEFAULT_RANGE, customFrom: null, customTo: null };
};

const parse = (search: URLSearchParams): PlatformErrorsFilters => {
    const environment = search.get("environment");
    const severity = search.get("severity");
    const category = search.get("category");
    return {
        environment: platformErrorsContract.isEnvironment(environment) ? environment : null,
        ...rangeOf(search),
        service: toDimension("service", search.get("service")),
        severity: platformErrorsContract.isSeverity(severity) ? severity : null,
        resourceType: toDimension("resourceType", search.get("resourceType")),
        errorCode: toDimension("errorCode", search.get("errorCode")),
        category: frontendErrorsFormat.isCategory(category) ? category : null,
    };
};

const toSearch = (filters: PlatformErrorsFilters): URLSearchParams => {
    const search = new URLSearchParams();
    if (filters.environment !== null) search.set("environment", filters.environment);
    if (filters.range === "custom") {
        search.set("range", "custom");
        if (filters.customFrom !== null) search.set("from", String(filters.customFrom));
        if (filters.customTo !== null) search.set("to", String(filters.customTo));
    } else if (filters.range !== DEFAULT_RANGE) {
        search.set("range", filters.range);
    }
    if (filters.service !== null) search.set("service", filters.service);
    if (filters.severity !== null) search.set("severity", filters.severity);
    if (filters.resourceType !== null) search.set("resourceType", filters.resourceType);
    if (filters.errorCode !== null) search.set("errorCode", filters.errorCode);
    if (filters.category !== null) search.set("category", filters.category);
    return search;
};

const retentionFloor = (now: number): number => now - RETENTION_MS + RETENTION_MARGIN_MS;

// A custom window keeps its most recent part: at most 7 days, never before the retained 13, never in the future.
const resolveRange = (filters: PlatformErrorsFilters, now: number): PlatformErrorsResolvedRange => {
    if (filters.range !== "custom") return { range: { from: now - PRESET_MS[filters.range], to: now }, clamped: false };
    if (filters.customFrom === null || filters.customTo === null || filters.customFrom >= filters.customTo) {
        return { range: null, clamped: false };
    }
    const to = Math.min(filters.customTo, now);
    const from = Math.max(filters.customFrom, retentionFloor(now), to - MAX_WINDOW_MS);
    if (from >= to) return { range: null, clamped: true };
    return { range: { from, to }, clamped: from !== filters.customFrom || to !== filters.customTo };
};

const toQuery = (filters: PlatformErrorsFilters, range: FrontendErrorsRange): PlatformErrorsQueryParams => ({
    environment: filters.environment,
    from: range.from,
    to: range.to,
    service: filters.service,
    severity: filters.severity,
    resourceType: filters.resourceType,
    errorCode: filters.errorCode,
    category: filters.category,
});

export const platformErrorsFilters = {
    PRESETS,
    parse,
    toSearch,
    toDimension,
    retentionFloor,
    resolveRange,
    toQuery,
};
